import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import '../styles/income-report.css';

const API_BASE = "https://api.mbsmartweb.com/api";

const IncomeReport = () => {
  const { incometype } = useParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [incomeData, setIncomeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [sortColumn, setSortColumn] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const itemsPerPage = entriesPerPage;

  // Helper function to normalize type (case-insensitive)
  const normalizeType = (type) => {
    const typeStr = String(type || '').toLowerCase();
    if (typeStr === 'credit') return 'Credit';
    if (typeStr === 'debit') return 'Debit';
    return type;
  };

  // Convert hyphenated URL param to spaced format for API
  const formatIncomeTypeForAPI = (type) => {
    if (!type) return 'all';
    return type.replace(/-/g, ' ');
  };

  // Format heading from URL param
  const formatHeading = () => {
    if (!incometype) return 'Income Report';
    return incometype
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ') + ' Report';
  };

  // Format subheading from URL param
  const formatSubheading = () => {
    if (!incometype) return 'Track your earnings and commissions';
    const type = incometype
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return `Track your ${type.toLowerCase()} earnings and commissions`;
  };

  // Truncate long text
  const truncateText = (text, maxLength = 80) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Fetch income history from API
  const fetchIncome = async (isRefresh = false) => {
    const UserId = localStorage.getItem("UserId");

    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError("");
    
    try {
      const apiIncomeType = formatIncomeTypeForAPI(incometype);
      
      const response = await axios.post(`${API_BASE}/incomehistory`, {
        UserId: UserId,
        income: apiIncomeType,
      });

      if (response.data.success) {
        if (response.data.data && response.data.data.length > 0) {
          const mappedData = response.data.data.map((item, index) => ({
            id: String(item.orderid || `INC${String(index + 1).padStart(3, '0')}`),
            type: normalizeType(item.Type), // Using normalizeType for consistency
            amount: parseFloat(item.amount) || 0,
            commission: parseFloat(item.commission) || 0,
            date: item.Time,
            formattedDate: item.Time ? formatDate(item.Time) : '-',
            fullDate: item.Time ? new Date(item.Time).toLocaleString() : '-',
            status: normalizeType(item.Type) === 'Credit' ? 'completed' : (normalizeType(item.Type) === 'Debit' ? 'pending' : 'completed'),
            from: String(item.Desc || '-'),
            mode: String(item.Mode || '-')
          }));
          setIncomeData(mappedData);
          setError("");
        } else {
          setIncomeData([]);
          setError("No Income records found");
        }
      } else {
        setIncomeData([]);
        setError(response.data.error || "No Income records found");
      }
    } catch (err) {
      console.error(err);
      setIncomeData([]);
      setError("Failed to fetch income data");
      toast.error("Failed to load income data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (incometype) {
      fetchIncome();
    }
  }, [incometype]);

  // Filter by search term (case-insensitive)
  const filteredTransactions = incomeData.filter(t => {
    const searchLower = searchTerm.toLowerCase();
    
    const idStr = String(t.id || '');
    const fromStr = String(t.from || '');
    const typeStr = String(t.type || '');
    const modeStr = String(t.mode || '');
    
    return idStr.toLowerCase().includes(searchLower) ||
           fromStr.toLowerCase().includes(searchLower) ||
           typeStr.toLowerCase().includes(searchLower) ||
           modeStr.toLowerCase().includes(searchLower);
  });

  // Sort transactions (case-insensitive for type)
  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    let aVal, bVal;
    switch (sortColumn) {
      case 'id':
        aVal = parseInt(a.id) || 0;
        bVal = parseInt(b.id) || 0;
        break;
      case 'amount':
        aVal = a.amount;
        bVal = b.amount;
        break;
      case 'date':
        aVal = new Date(a.date);
        bVal = new Date(b.date);
        break;
      case 'type':
        aVal = String(a.type || '').toLowerCase();
        bVal = String(b.type || '').toLowerCase();
        break;
      default:
        aVal = a.date;
        bVal = b.date;
    }
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination
  const paginatedTransactions = sortedTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(sortedTransactions.length / itemsPerPage);
  
  // Case-insensitive total amount calculation
  const totalAmount = incomeData.reduce((sum, t) => {
    const typeNormalized = normalizeType(t.type);
    return sum + (typeNormalized === 'Credit' ? t.amount : 0);
  }, 0);
  
  const totalCommission = incomeData.reduce((sum, t) => sum + (t.commission || 0), 0);

  // Case-insensitive getTypeIcon
  const getTypeIcon = (type) => {
    const typeStr = String(type || '').toLowerCase();
    if (typeStr === 'credit') return 'fa-arrow-up';
    if (typeStr === 'debit') return 'fa-arrow-down';
    if (typeStr.includes('profit')) return 'fa-chart-line';
    if (typeStr.includes('commission')) return 'fa-users';
    if (typeStr.includes('bonus')) return 'fa-gift';
    return 'fa-money';
  };

  // Handle entries per page change
  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Handle sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Export to CSV
  const exportToCSV = () => {
    if (filteredTransactions.length === 0) {
      toast.warning("No data to export");
      return;
    }
    
    const headers = ['ID', 'Type', 'Source', 'Amount', 'Mode', 'Date', 'Status'];
    const csvData = filteredTransactions.map(transaction => [
      transaction.id,
      transaction.type,
      transaction.from.replace(/,/g, ';'),
      `$${transaction.amount.toFixed(2)}`,
      transaction.mode,
      transaction.fullDate,
      transaction.status
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${incometype}_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Report exported successfully!");
  };

  // Mode Badge Component
  const ModeBadge = ({ mode }) => {
    let bgColor = "#6b7280";
    const modeStr = String(mode || '').toLowerCase();
    switch (modeStr) {
      case 'wallet':
        bgColor = "#3b82f6";
        break;
      case 'online':
        bgColor = "#8b5cf6";
        break;
      case 'bank':
        bgColor = "#f59e0b";
        break;
      default:
        bgColor = "#6b7280";
    }
    return (
      <span className="mode-badge" style={{ backgroundColor: bgColor }}>
        {mode}
      </span>
    );
  };

  // Reset pagination when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortColumn !== column) return <i className="fa fa-sort"></i>;
    return sortOrder === 'asc' ? <i className="fa fa-sort-up"></i> : <i className="fa fa-sort-down"></i>;
  };

  return (
    <Layout>
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="income-report-page">
        {/* Page Header */}
        <div className="page-header">
          <div className="header-left">
            <h1>{formatHeading()}</h1>
            <p>{formatSubheading()}</p>
          </div>
          <div className="stats-summary">
            <div className="stat-card total-amount">
              <span>Total Amount</span>
              <strong>${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
            </div>
            <div className="stat-card total-commission">
              <span>Total Commission</span>
              <strong>${totalCommission.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
            </div>
            <div className="stat-card total-transactions">
              <span>Transactions</span>
              <strong>{filteredTransactions.length}</strong>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="search-box">
            <i className="fa fa-search"></i>
            <input
              type="text"
              placeholder="Search by ID, Source, Type or Mode..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>
                <i className="fa fa-times"></i>
              </button>
            )}
          </div>
          
          <div className="filter-group">
            <select value={entriesPerPage} onChange={handleEntriesChange}>
              <option value={10}>10 entries</option>
              <option value={25}>25 entries</option>
              <option value={50}>50 entries</option>
              <option value={100}>100 entries</option>
            </select>
            <button 
              className={`refresh-btn ${refreshing ? 'loading' : ''}`} 
              onClick={() => fetchIncome(true)}
              disabled={refreshing}
            >
              <i className="fa fa-refresh"></i> {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <button className="export-btn" onClick={exportToCSV}>
              <i className="fa fa-download"></i> Export
            </button>
          </div>
        </div>

        {/* Table Container */}
        <div className="table-container">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading transactions...</p>
            </div>
          ) : error ? (
            <div className="empty-state">
              <i className="fa fa-exclamation-circle"></i>
              <p>{error}</p>
              <button className="btn-primary" onClick={() => fetchIncome()}>
                Try Again
              </button>
            </div>
          ) : paginatedTransactions.length === 0 ? (
            <div className="empty-state">
              <i className="fa fa-inbox"></i>
              <p>No transactions found</p>
              <small>Try adjusting your search or filter criteria</small>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="income-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('id')} className="sortable">
                        ID {getSortIcon('id')}
                      </th>
                      <th onClick={() => handleSort('type')} className="sortable">
                        Type {getSortIcon('type')}
                      </th>
                      <th>Source</th>
                      <th onClick={() => handleSort('amount')} className="sortable amount-sort">
                        Amount {getSortIcon('amount')}
                      </th>
                      <th>Mode</th>
                      <th onClick={() => handleSort('date')} className="sortable">
                        Date {getSortIcon('date')}
                      </th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTransactions.map((transaction, idx) => (
                      <tr key={transaction.id}>
                        <td className="col-id">
                          <span className="transaction-id">#{transaction.id}</span>
                        </td>
                        <td className="col-type">
                          <div className="transaction-type">
                            <i className={`fa ${getTypeIcon(transaction.type)}`}></i>
                            {transaction.type}
                          </div>
                        </td>
                        <td className="col-source" title={transaction.from}>
                          {truncateText(transaction.from, 60)}
                        </td>
                        <td className={`col-amount amount ${normalizeType(transaction.type) === 'Credit' ? 'positive' : 'negative'}`}>
                          {normalizeType(transaction.type) === 'Credit' ? '+' : '-'}${transaction.amount.toFixed(2)}
                        </td>
                        <td className="col-mode">
                          <ModeBadge mode={transaction.mode} />
                        </td>
                        <td className="col-date">{transaction.formattedDate}</td>
                        <td className="col-status">
                          <span className={`status-badge ${transaction.status}`}>
                            {transaction.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="table-footer">
                  <div className="table-info">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedTransactions.length)} of {sortedTransactions.length} entries
                  </div>
                  <div className="pagination">
                    <button 
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      <i className="fa fa-chevron-left"></i>
                    </button>
                    {(() => {
                      let pages = [];
                      let startPage = Math.max(1, currentPage - 2);
                      let endPage = Math.min(totalPages, startPage + 4);
                      
                      if (endPage - startPage < 4 && startPage > 1) {
                        startPage = Math.max(1, endPage - 4);
                      }
                      
                      if (startPage > 1) {
                        pages.push(
                          <button key={1} onClick={() => setCurrentPage(1)} className="pagination-btn">1</button>
                        );
                        if (startPage > 2) {
                          pages.push(<span key="dots1" className="pagination-dots">...</span>);
                        }
                      }
                      
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button 
                            key={i} 
                            onClick={() => setCurrentPage(i)} 
                            className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
                          >
                            {i}
                          </button>
                        );
                      }
                      
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(<span key="dots2" className="pagination-dots">...</span>);
                        }
                        pages.push(
                          <button key={totalPages} onClick={() => setCurrentPage(totalPages)} className="pagination-btn">
                            {totalPages}
                          </button>
                        );
                      }
                      
                      return pages;
                    })()}
                    <button 
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      <i className="fa fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default IncomeReport;